import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductGallery } from '@/components/product/ProductGallery';
import { ProductDetail } from '@/components/product/ProductDetail';
import { ProductInfo } from '@/components/product/ProductInfo';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Alert, AlertDescription } from '@/components/ui/alert';
import productData from '@/data/products';
import { Home, ChevronRight, Info } from 'lucide-react';

export function ProductPage() {
  const navigate = useNavigate();
  const product = productData; // 單一產品

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-screen-xl">
        {/* 麵包屑導航 */}
        <Breadcrumb className="mb-8">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate('/')}
                className="flex items-center gap-1 cursor-pointer hover:text-blue-600"
              >
                <Home className="h-4 w-4" />
                首頁
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <ChevronRight className="h-4 w-4" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>產品</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* FDA 最新立場提示 */}
        <Alert className="mb-8 border-blue-200 bg-blue-50">
          <Info className="h-5 w-5 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>FDA 最新立場：</strong>
            2025年9月29日，美國 FDA 確認「NMN 符合膳食補充劑的定義」，為 NMN 產品的合法地位提供明確保障。
          </AlertDescription>
        </Alert>

        {/* 產品主要內容區 */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
          {/* 左側：產品圖片 */}
          <div>
            <ProductGallery images={product.images} />
          </div>

          {/* 右側：產品資訊與購買 */}
          <div>
            <ProductDetail product={product} />
          </div>
        </div>

        {/* 產品詳細資訊（Tabs） */}
        <div>
          <ProductInfo product={product} />
        </div>

        {/* 科學背景說明 */}
        <div className="mt-12 bg-white rounded-lg p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            NMN 與 NAD⁺ 的關係
          </h2>
          <div className="prose max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              人體由約 40～60 兆個細胞組成，細胞會持續更新，以維持正常生理運作。
              NAD⁺（菸鹼醯胺腺嘌呤二核苷酸）是能量代謝過程中的重要輔酶，參與多項生化反應。
              隨著年齡與生活型態變化，體內 NAD⁺ 的生成效率可能逐漸降低，進而影響能量運用與代謝平衡。
            </p>
            <p>
              NMN（β-菸醯胺單核苷酸）是 NAD⁺ 的前體之一。
              將 NMN 視為日常營養補充的一環，並配合均衡飲食、規律運動與良好作息，有助維持正常代謝與日常活力。
            </p>
          </div>
        </div>

        {/* 安全與認證資訊 */}
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg p-6 text-center shadow-sm">
            <div className="text-4xl mb-3">🇺🇸</div>
            <h3 className="font-semibold mb-2">美國製造</h3>
            <p className="text-sm text-gray-600">cGMP 品質保證</p>
          </div>
          <div className="bg-white rounded-lg p-6 text-center shadow-sm">
            <div className="text-4xl mb-3">✓</div>
            <h3 className="font-semibold mb-2">FDA 認證</h3>
            <p className="text-sm text-gray-600">安全可靠</p>
          </div>
          <div className="bg-white rounded-lg p-6 text-center shadow-sm">
            <div className="text-4xl mb-3">🛡️</div>
            <h3 className="font-semibold mb-2">產品責任險</h3>
            <p className="text-sm text-gray-600">2000 萬元保障</p>
          </div>
        </div>
      </div>
    </div>
  );
}
